import React from 'react';
import { Pressable, View } from 'react-native';
import { Link } from 'expo-router';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { APP_STORE_URL, GOOGLE_PLAY_URL, openStoreUrl } from '@/components/PublicFooter/publicFooterStoreLinks';
import { PrimaryCta, useInteractive, useLanding } from './LandingKit';
import LandingHeroComposer from './LandingHeroComposer';
import PageVideo from '@/components/Tutorials/PageVideo';
import { neutralColors } from '@/constants/neutralColors';
import ReferralAppHandoff from './ReferralAppHandoff';
import { content } from '@/lib/landingPageSeo';
import { LAYOUT, fluid, isWeb } from './landingTokens';

const PLATFORMS = [
  { label: 'Mac', short: 'Mac', icon: 'laptop', href: '/mac' },
  { label: 'iPhone & iPad', short: 'iPhone', icon: 'mobile-screen-button', url: APP_STORE_URL, mobile: true },
  { label: 'Android', short: 'Android', icon: 'mobile-screen-button', url: GOOGLE_PLAY_URL, mobile: true },
  { label: 'Chrome & Brave', short: 'Chrome', icon: 'globe', href: '/extension' },
];

// Marketing pages never navigate away on their own; the end card waits for a click.
const HERO_VIDEO_NEXT = { label: 'Learn to use Super Intelligence with free lessons', href: '/learn', autoplay: false, action: 'Learn about Super Intelligence' };
const HERO_VIDEO_SIGNUP = { label: 'Create a free account', href: '/signup', title: 'Put Super Intelligence to work for you.' };
const VISUALLY_HIDDEN = { position: 'absolute', width: 1, height: 1, margin: -1, padding: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0 };

function PlatformLink({ platform }) {
  const { isDark, isXS } = useLanding();
  const colors = neutralColors({}, isDark);
  const { hovered, handlers } = useInteractive();
  const color = hovered ? colors.text_color_primary : colors.text_color_secondary;
  const link = (
    <Pressable
      {...handlers}
      onPress={platform.url ? () => openStoreUrl(platform.url) : undefined}
      accessibilityRole="link"
      accessibilityLabel={`Gab AI for ${platform.label}`}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 6, minHeight: 32, ...(isWeb ? { cursor: 'pointer' } : {}) }}
    >
      <Icon name={platform.icon} size={12} color={color} />
      <Text weight="medium" style={{ color, fontSize: 13, ...(isWeb ? { textDecorationLine: 'underline' } : {}) }}>{isXS ? platform.short : platform.label}</Text>
    </Pressable>
  );
  return platform.href ? <Link href={platform.href} asChild>{link}</Link> : link;
}

function HeroPlatforms() {
  const { isDark, isXS } = useLanding();
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', columnGap: isXS ? 13 : 18, rowGap: 2 }}>
      <Text weight="medium" style={{ color: isDark ? '#FFFFFF' : '#040404', fontSize: 13 }}>Download our apps</Text>
      {PLATFORMS.filter(platform => !isXS || platform.mobile).map(platform => <PlatformLink key={platform.label} platform={platform} />)}
    </View>
  );
}

function LandingEditorialHero({ topInset = 0 }) {
  const { isDark, width, isXS, pad, display } = useLanding();
  const colors = neutralColors({}, isDark);
  const headline = display({ min: 46, max: 104, leading: 1.04, tracking: -0.055 });

  return (
    <View accessibilityLabel="Gab AI" {...(isWeb ? { dataSet: { landingHero: 'true' } } : {})} style={{ width: '100%', alignItems: 'center', backgroundColor: isDark ? '#040404' : '#FFFFFF', position: 'relative', overflow: 'hidden' }}>
      <View style={{ width: '100%', maxWidth: LAYOUT.max, paddingHorizontal: pad, paddingTop: topInset + (isWeb ? fluid(width, 88, 112) : fluid(width, 112, 160)), paddingBottom: fluid(width, 80, 112), alignItems: 'center' }}>
        <Text tag="h1" weight="extraBold" style={isWeb ? VISUALLY_HIDDEN : { color: colors.text_color_primary, ...headline, maxWidth: '100%', textAlign: 'center' }}>
          {content.headline}
        </Text>

        {isWeb && (
          <View style={{ width: '100%', maxWidth: 640 }}>
            <PageVideo page="home" next={HERO_VIDEO_NEXT} signup={HERO_VIDEO_SIGNUP} playButton className="page-video page-video-hero" />
          </View>
        )}
        <LandingHeroComposer style={{ marginTop: isWeb ? fluid(width, 24, 32) : fluid(width, 36, 48) }} />

        <View style={{ alignItems: 'center', gap: isXS ? 8 : 10, marginTop: fluid(width, 22, 28), paddingVertical: isXS ? 10 : 8, paddingHorizontal: isXS ? 14 : 22, width: '100%', maxWidth: 680 }}>
          <HeroPlatforms />
          <ReferralAppHandoff />
        </View>
        <View style={{ marginTop: fluid(width, 20, 26), alignItems: 'center', width: isXS ? '100%' : undefined, maxWidth: 680 }}>
          <PrimaryCta label="Sign up for free" full={isXS} />
        </View>
      </View>
    </View>
  );
}

export default React.memo(LandingEditorialHero);
